import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail } from 'lucide-react';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/api';
import { usePageSEO } from '../lib/usePageSEO';

export default function ForgotPassword() {
  usePageSEO({
    title: 'Reset your password — MyLeaf Legacy',
    description: 'Enter your email to receive a secure password reset link for your MyLeaf Legacy account.',
    path: '/forgot-password',
  });
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email: email.trim() },
        { withCredentials: true },
      );
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-[hsl(var(--primary))] text-[#FDFBF7] overflow-hidden">
        <Link to="/" className="inline-flex" data-testid="forgot-side-logo-link">
          <Logo tone="cream" size={176} />
        </Link>
        <div>
          <div className="ml-overline !text-[#D9B775] !font-semibold">Reset access</div>
          <h1 className="font-serif text-5xl mt-4 leading-tight font-semibold">It happens. Let's get you back in.</h1>
          <p className="mt-5 text-[#FDFBF7]/90 max-w-md font-medium">
            Enter your email and we'll send you a quiet link. The link is good for thirty minutes.
          </p>
        </div>
        <div className="text-xs text-[#FDFBF7]/70 font-medium">© MyLeaf Legacy — Forever Here</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md" data-testid="forgot-password-page">
          <div className="md:hidden mb-8"><Link to="/"><Logo size={128} /></Link></div>
          <div className="ml-overline !text-[hsl(var(--primary))] !font-semibold">Forgot password</div>
          <h2 className="font-serif text-4xl mt-2 text-[hsl(var(--primary))] font-semibold">Reset your password.</h2>

          {done ? (
            <div
              className="mt-8 rounded-md border border-[hsl(var(--accent))]/55 bg-[hsl(var(--accent)/0.08)] px-5 py-6"
              data-testid="forgot-password-sent"
            >
              <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Check your inbox</h3>
              <p className="mt-3 text-sm text-[hsl(var(--primary)/0.78)] font-medium leading-relaxed">
                If an account exists for that email, we've sent you a link to set a new password. The link is good for thirty minutes.
              </p>
              <Link to="/login" className="mt-5 inline-flex ml-btn-outline" data-testid="forgot-back-to-login">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4 wizard-readable">
              <div>
                <label className="ml-label">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.55)]" />
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="ml-input pl-11" placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {err && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="forgot-error">{err}</div>}
              <button
                type="submit"
                disabled={submitting || !email}
                className="ml-btn-primary w-full !py-3.5"
                data-testid="forgot-submit-btn"
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
              <p className="text-sm text-[hsl(var(--primary)/0.78)] font-medium pt-2">
                Remembered it? <Link to="/login" className="text-[hsl(var(--primary))] font-semibold" data-testid="forgot-back-link">Back to sign in →</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
